import React from 'react'
import { useTheme } from '../../middleware/ThemeContext';
import styled from 'styled-components';
import { CheckCircle } from '@mui/icons-material';
import StyledText from '../../components/StyledComponents/StyledText';
import { formatDateDefault } from '../../utils/helper';
import { getCleanFileName } from '../../components/Media/FileUploadPreview';
// import { getCleanFileName } from '../../components/Media/FileUploadPreview';

interface FieldProps {
    isActive: boolean;
    field_key: string;
    label: string;
    data: string;
    data_id: number;
    rolesData?: {
        data_id: number;
        roleids: number[];
        roleNames: string;
    };
    organizationData?: {data_id: number, id: number, name: string, image: string} | {};
    fieldData?: {id: number, name: string, data_type?: string, value: string};
    updateMeta?: (key: string, value: any) => void;
    updateFields?: (data_id: number, id: number, name: string, value: string) => void;
    handelChange: (e: React.ChangeEvent<any>) => void;
    onClick: (field_key: string, value: string) => void;
}

const FILE_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 
  'ppt', 'pptx', 'txt', 'zip', 'rar', '7z'
];

/**
 * Extracts filename with extension from path/URL if it has a valid file extension
 */
function getFileName(pathOrUrl: string): string {
  if (!pathOrUrl || typeof pathOrUrl !== 'string') return '-';

  try {
    // Extract pathname from URL if it's a full URL
    const pathname = pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')
      ? new URL(pathOrUrl).pathname
      : pathOrUrl;

    // Get last segment after splitting by '/'
    const segments = pathname.replace(/\/+$/, '').split('/');
    const filename = segments.pop()?.trim();

    if (!filename || !filename.includes('.')) return '-';

    // Extract and validate extension
    const ext = filename.split('.').pop()?.toLowerCase();
    if (!ext || !FILE_EXTENSIONS.includes(ext)) return '-';

    return getCleanFileName(filename);
  } catch {
    return '-';
  }
}

export function formatDisplayValue(data: any, label: string, data_type?: string): string {
  if (data === null || data === undefined || data === '') return '-';

  // Handle DATE_TIME type
  if (data_type === 'DATE_TIME') {
    const lowerLabel = label.toLowerCase();
    const isDate = lowerLabel.includes('date');
    const isTime = lowerLabel.includes('time');

    if (isDate || isTime) {
      const dateObj = new Date(data);
      if (isNaN(dateObj.getTime())) {
        return '-';
      }
      return formatDateDefault(data, isTime);
    }
  }

  // Auto-detect FILE_UPLOAD: check if data looks like a file path/URL with valid extension
  if (typeof data === 'string') {
    const cleanName = getFileName(data);
    if (cleanName !== '-') {
      return cleanName;
    }
  }

  // Handle explicit FILE_UPLOAD type (fallback)
  if (data_type === 'FILE_UPLOAD') {
    return getFileName(data);
  }

  // Handle arrays (already parsed or native)
  if (Array.isArray(data)) {
    return data.map(item => String(item)).join(', ');
  }

  // Try parsing JSON strings
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) {
        return parsed.map(item => String(item)).join(', ');
      }
      if (typeof parsed === 'object' && parsed !== null) {
        return JSON.stringify(parsed);
      }
      return String(parsed);
    } catch {
      // Not JSON, return trimmed string
      return data.trim();
    }
  }

  // Handle objects
  if (typeof data === 'object') {
    try {
      return JSON.stringify(data);
    } catch {
      return String(data);
    }
  }

  // Fallback for primitives (number, boolean, etc.)
  return String(data);
}

const FieldWithRadio: React.FC<FieldProps> = ({isActive, rolesData, field_key, label, data, data_id, organizationData, fieldData, updateMeta, updateFields, handelChange, onClick}) => {
    const {theme} = useTheme();
        
        const displayValue = formatDisplayValue(data, label, fieldData?.data_type);

        const handleClick = () => {
            if (data === '-') return;
            const the_Data = {
                data_id: data_id,
                value: data,
            }
            if (updateMeta) {
            const value =
                field_key === 'contactRoles' && rolesData
                ? rolesData
                : field_key === 'contactOrganizations' && organizationData
                ? organizationData
                : the_Data;
            updateMeta(field_key, value);
            } else if (updateFields && fieldData) {
            updateFields(data_id, fieldData.id, fieldData.name, fieldData.value);
            }

            onClick(field_key, data_id+'_'+displayValue);

        };

  return (
    <FieldCon 
        theme={theme}
        onClick={handleClick}
        className={isActive ? 'active' : ''}
    >
        <div style={{ width: 'calc(100% - 24px)', overflow: 'hidden' }}>
            <StyledText fontSize='12px' fontWeight={500} className='mb-1'>{label}</StyledText>
            <StyledText className='field-data' fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} htmlTag={'p'} style={{ overflow: 'hidden' }}>
                {displayValue}
            </StyledText>
        </div>
        <div className='check'>
            {data !== '-' && data !== ' ' &&
                <>
                    <CheckCircle fontSize='small' htmlColor={isActive ? theme.CorporateTheme.colors.primary : theme.CorporateTheme.colors.border} />
                    <input type='radio' name={field_key} value={data_id+'_'+displayValue} checked={isActive} onChange={handelChange} style={{ display: 'none' }}   />
                    {/* <ErrorMessage name={field_key} /> */}
                </>
            }
        </div>
    </FieldCon>
  )
}

export default FieldWithRadio

const FieldCon = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 5px;
    cursor: pointer;
    &:hover {
        background-color: ${({theme}) => theme.CorporateTheme.colors.bannerBg};
    }
    &.active {
        background-color: ${({theme}) => theme.CorporateTheme.colors.bannerBg};
        border-radius: 5px;
    }
    & .field-data{
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    // margin: 10px 0px;
`;